import ExcelJS from "exceljs";
import {
  addSectionHeader,
  styleColumnHeaderRow,
} from "../../ExportExcelButtonV3/utils";
import type { BuildRoomsWorkbook } from "./types";

// Same palette as the sibling export sheets (useExportHandlers)
const SECTION_HEADER_BG = "FFD6E4F7";
const SECTION_HEADER_FONT = "FF003A8C";
const NOTE_FONT_COLOR = "FF6B7280";
const TITLE_FONT_SIZE = 14;
const POSITION_COLUMN_KEYS = ["positionNumber", "description"];

const addMergedRow = (
  sheet: ExcelJS.Worksheet,
  text: string,
  font: Partial<ExcelJS.Font>,
) => {
  const row = sheet.addRow({ positionNumber: text });
  sheet.mergeCells(row.number, 1, row.number, sheet.columns.length);
  row.getCell(1).font = font;
};

const mergeColumnDown = (
  sheet: ExcelJS.Worksheet,
  key: string,
  rows: ExcelJS.Row[],
) => {
  if (rows.length < 2) return;
  const columnNumber = sheet.getColumn(key).number;
  sheet.mergeCells(
    rows[0].number,
    columnNumber,
    rows[rows.length - 1].number,
    columnNumber,
  );
};

export const buildRoomsWorkbook: BuildRoomsWorkbook = ({
  planName,
  roomPositions,
  labels,
  formatRoomTotal,
}) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(labels.sheetName);

  sheet.columns = [
    { key: "positionNumber", width: 14 },
    { key: "description", width: 60 },
    { key: "detectedType", width: 34 },
    { key: "count", width: 12 },
    { key: "total", width: 12 },
  ];

  const hasRooms = roomPositions.length > 0;
  addMergedRow(sheet, planName, { bold: true, size: TITLE_FONT_SIZE });
  addMergedRow(sheet, hasRooms ? labels.note : labels.noRooms, {
    italic: true,
    color: { argb: NOTE_FONT_COLOR },
  });
  if (!hasRooms) return workbook;
  sheet.addRow({});

  const headerRow = sheet.addRow({
    positionNumber: labels.position,
    description: labels.description,
    detectedType: labels.detectedType,
    count: labels.count,
    total: labels.total,
  });
  styleColumnHeaderRow(headerRow);

  for (const { room, total, positions } of roomPositions) {
    addSectionHeader(
      sheet,
      `${room.name} — ${formatRoomTotal(total)}`,
      SECTION_HEADER_BG,
      SECTION_HEADER_FONT,
    );
    const roomRows: ExcelJS.Row[] = [];
    for (const position of positions) {
      // The position spans one sheet row per group; its own cells are merged down over them
      const positionRows = position.groups.map((group, index) => {
        const groupCells = { detectedType: group.label, count: group.quantity };
        const addedRow = sheet.addRow(
          index === 0
            ? {
                positionNumber: position.positionNumber ?? undefined,
                description: position.description ?? undefined,
                ...groupCells,
              }
            : groupCells,
        );
        addedRow.alignment = { wrapText: true, vertical: "top" };
        return addedRow;
      });
      for (const key of POSITION_COLUMN_KEYS) {
        mergeColumnDown(sheet, key, positionRows);
      }
      roomRows.push(...positionRows);
    }
    if (roomRows.length === 0) continue;
    // The room total spans every row of the room, like the position cells span their groups
    const totalCell = roomRows[0].getCell("total");
    totalCell.value = total;
    totalCell.font = { bold: true };
    mergeColumnDown(sheet, "total", roomRows);
  }

  return workbook;
};
